import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrgStore } from '@/stores/org-store';
import {
  createSchedule,
  updateSchedule,
  deleteSchedule,
  pauseSchedule,
  resumeSchedule,
  triggerSchedule,
  type CreateScheduleRequest,
  type UpdateScheduleRequest,
} from '@/services/workflow/schedule';

const useScheduleInvalidation = () => {
  const { currentOrg } = useOrgStore();
  const orgId = currentOrg?.orgid;
  const queryClient = useQueryClient();
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['org-schedules', orgId] });
    queryClient.invalidateQueries({ queryKey: ['scheduled-runs', orgId] });
    queryClient.invalidateQueries({ queryKey: ['scheduled-run-days', orgId] });
  };
  return { orgId, invalidate };
};

export const useCreateSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string;
      data: CreateScheduleRequest;
    }) => createSchedule(orgId!, workflowId, data),
    onSuccess: invalidate,
  });
};

export const useUpdateSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      scheduleId,
      data,
    }: {
      workflowId: string;
      scheduleId: string;
      data: UpdateScheduleRequest;
    }) => updateSchedule(orgId!, workflowId, scheduleId, data),
    onSuccess: invalidate,
  });
};

export const useDeleteSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      scheduleId,
    }: {
      workflowId: string;
      scheduleId: string;
    }) => deleteSchedule(orgId!, workflowId, scheduleId),
    onSuccess: invalidate,
  });
};

export const usePauseSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      scheduleId,
    }: {
      workflowId: string;
      scheduleId: string;
    }) => pauseSchedule(orgId!, workflowId, scheduleId),
    onSuccess: invalidate,
  });
};

export const useResumeSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      scheduleId,
    }: {
      workflowId: string;
      scheduleId: string;
    }) => resumeSchedule(orgId!, workflowId, scheduleId),
    onSuccess: invalidate,
  });
};

export const useTriggerSchedule = () => {
  const { orgId, invalidate } = useScheduleInvalidation();
  return useMutation({
    mutationFn: ({
      workflowId,
      scheduleId,
    }: {
      workflowId: string;
      scheduleId: string;
    }) => triggerSchedule(orgId!, workflowId, scheduleId),
    onSuccess: invalidate,
  });
};
